import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
    type LoginCredentials,
    type RegisterCredentials,
    type Organisation,
    type RoleNameType,
    type User,
    type Role,
    type LoginCategory,
    Platform,
    RoleName
} from '@kiddocaremy/sdk-ts'
import { sdk } from '@/lib/sdk'
import { setSentryUser } from '@/lib/sentry'
import { useFeatureFlagsStore } from '@/stores/featureFlags'

export const useAuthStore = defineStore('auth', () => {
    const user = ref<User | null>(null)
    const organizations = ref<Organisation[]>([])
    const selectedOrganization = ref<Organisation | null>(null)
    const isInitialized = ref(false)

    // Mirrors the init payload's talentHubConsent block. Null until init has run, so a component
    // can tell "not loaded yet" from "nothing to claim".
    const talentHubConsent = ref<{ pending: boolean; claimDeclined: boolean } | null>(null)

    const isAuthenticated = computed(() => !!user.value)

    async function initializeAuth(loginCategoryParam?: LoginCategory) {
        const featureFlagsStore = useFeatureFlagsStore()

        try {
            await sdk.auth.getCsrfCookie(Platform.ID_CONNECT, loginCategoryParam)
            const response = await sdk.general.init(Platform.ID_CONNECT,
                loginCategoryParam ? { login_category: loginCategoryParam } : undefined
            )
            user.value = response.data.user as unknown as User
            organizations.value = response.data.organizations || []
            featureFlagsStore.setFlags((response.data as any).featureFlags)
            talentHubConsent.value = response.data.talentHubConsent ?? null

            if (user.value) {
                setSentryUser({ id: user.value.id, email: user.value.email, name: user.value.name })
            }

            const savedOrgCode = localStorage.getItem('selected_org_code')
            if (savedOrgCode && organizations.value.length > 0) {
                selectedOrganization.value = organizations.value.find(org => org.code === savedOrgCode) || null
            }

            isInitialized.value = true
            return response.data
        } catch (error) {
            user.value = null
            organizations.value = []
            selectedOrganization.value = null
            talentHubConsent.value = null

            // A 401 here means the current session isn't authorised for this area:
            // no session at all, or a session for another category (e.g. an
            // organisation-only account landing on the talent portal). Treat it as a
            // clean logged-out state so the guest/login page renders normally instead
            // of surfacing a raw "Unauthorized for this platform" error.
            if ((error as { response?: { status?: number } })?.response?.status === 401) {
                isInitialized.value = true
                return null
            }

            // Any other failure (e.g. a 422 when no loginCategoryParam is available,
            // such as the very first navigation landing on a route with no /care or
            // /talent prefix) leaves isInitialized false on purpose, so the next
            // navigation - which may resolve a real login category from the route -
            // gets to retry instead of permanently freezing feature flags at null.
            throw error
        }
    }

    async function login(credentials: LoginCredentials) {
        const featureFlagsStore = useFeatureFlagsStore()

        if (credentials.platform_type === Platform.TYPE_WEB) {
            await sdk.auth.getCsrfCookie(Platform.ID_CONNECT, credentials.login_category)
        }

        await sdk.auth.login(credentials)

        const response = await sdk.general.init(Platform.ID_CONNECT, {
            login_category: credentials.login_category
        })
        user.value = response.data.user as unknown as User
        organizations.value = response.data.organizations || []
        featureFlagsStore.setFlags((response.data as any).featureFlags)
        talentHubConsent.value = response.data.talentHubConsent ?? null

        if (user.value) {
            setSentryUser({ id: user.value.id, email: user.value.email, name: user.value.name })
        }

        // Auto-select if there's only one organization
        if (organizations.value.length === 1) {
            selectOrganization(organizations.value[0])
        }

        return response.data
    }

    async function register(credentials: RegisterCredentials) {
        const featureFlagsStore = useFeatureFlagsStore()

        if (credentials.platform_type === Platform.TYPE_WEB) {
            await sdk.auth.getCsrfCookie(Platform.ID_CONNECT, credentials.login_category)
        }

        await sdk.auth.register(credentials)

        const response = await sdk.general.init(Platform.ID_CONNECT, {
            login_category: credentials.login_category
        })
        user.value = response.data.user as unknown as User
        organizations.value = response.data.organizations || []
        featureFlagsStore.setFlags((response.data as any).featureFlags)

        if (user.value) {
            setSentryUser({ id: user.value.id, email: user.value.email, name: user.value.name })
        }

        // Auto-select if there's only one organization
        if (organizations.value.length === 1) {
            selectOrganization(organizations.value[0])
        }

        return response.data
    }

    async function loginViaGoogle(loginCategory: LoginCategory) {
        const response = await sdk.auth.loginViaGoogle(Platform.ID_CONNECT, loginCategory)

        // Store login category in sessionStorage as backup
        sessionStorage.setItem('google_login_category', loginCategory)

        return response
    }

    async function logout() {
        const featureFlagsStore = useFeatureFlagsStore()

        await sdk.auth.logout()
        user.value = null
        organizations.value = []
        selectedOrganization.value = null
        talentHubConsent.value = null
        localStorage.removeItem('selected_org_code')
        setSentryUser(null)
        featureFlagsStore.reset()
    }

    function selectOrganization(org: Organisation) {
        selectedOrganization.value = org
        localStorage.setItem('selected_org_code', org.code)
    }

    function clearSelectedOrganization() {
        selectedOrganization.value = null
        localStorage.removeItem('selected_org_code')
    }

    function hasRole(role: string | string[]): boolean {
        if (!user.value) return false

        // Handle array of roles - check if user has at least one
        if (Array.isArray(role)) {
            return role.some((r: string) => hasRole(r))
        }

        // Check roles array
        if (user.value.roles && Array.isArray(user.value.roles)) {
            return user.value.roles.some((r: Role) => r.name === role)
        }

        return false
    }

    function hasAnyRole(roles: string[]): boolean {
        return roles.some(role => hasRole(role))
    }

    function hasAllRoles(roles: string[]): boolean {
        return roles.every(role => hasRole(role))
    }

    function hasPermission(permission: string): boolean {
        return user.value?.permissions?.includes(permission) || false
    }

    function hasAnyPermission(permissions: string[]): boolean {
        return permissions.some(permission => hasPermission(permission))
    }

    function hasAllPermissions(permissions: string[]): boolean {
        return permissions.every(permission => hasPermission(permission))
    }

    function getVerificationRoute(area?: 'care' | 'talent'): string | null {
        if (!user.value) return null

        // If no area specified, try to determine from user's role
        if (!area) {
            const isOrgUser = hasAnyRole([RoleName.ORGANISATION_OWNER, RoleName.ORGANISATION_ADMIN])
            area = isOrgUser ? 'care' : 'talent'
        }

        // Check email verification first
        if (!user.value.emailVerifiedAt) {
            // Redirect to dashboard home where verification dialog will appear
            if (area === 'care') {
                // For care users, redirect to their org dashboard
                // Auto-select if there's only one organization
                if (!selectedOrganization.value && organizations.value.length === 1) {
                    selectOrganization(organizations.value[0])
                    return `/care/${organizations.value[0].code}/dashboard`
                }

                if (!selectedOrganization.value && organizations.value.length > 0) {
                    return '/care/select-org'
                }
                return selectedOrganization.value
                    ? `/care/${selectedOrganization.value.code}/dashboard`
                    : '/care/select-org'
            } else {
                // For talent users, redirect to dashboard
                return '/talent/dashboard'
            }
        }

        // Then check phone verification (if enabled)
        const phoneVerificationEnabled = import.meta.env.VITE_ENABLE_PHONE_VERIFICATION !== 'false'

        if (phoneVerificationEnabled) {
            // For talent users, check mainProfile.phoneVerifiedAt
            // For organisation users, check organizationProfile.phoneVerifiedAt
            const phoneVerified = area === 'talent'
                ? user.value.mainProfile?.phoneVerifiedAt
                : (user.value as any).organizationProfile?.phoneVerifiedAt

            if (!phoneVerified) {
                // Redirect to dashboard home where verification dialog will appear
                if (area === 'care') {
                    // For care users, redirect to their org dashboard
                    // Auto-select if there's only one organization
                    if (!selectedOrganization.value && organizations.value.length === 1) {
                        selectOrganization(organizations.value[0])
                        return `/care/${organizations.value[0].code}/dashboard`
                    }

                    if (!selectedOrganization.value && organizations.value.length > 0) {
                        return '/care/select-org'
                    }
                    return selectedOrganization.value
                        ? `/care/${selectedOrganization.value.code}/dashboard`
                        : '/care/select-org'
                } else {
                    return '/talent/dashboard'
                }
            }
        }

        // All required verifications complete
        return null
    }

    function getDefaultRoute(): string {
        if (!user.value) return '/login'

        // Determine area based on user roles
        const isOrgUser = hasAnyRole([RoleName.ORGANISATION_OWNER, RoleName.ORGANISATION_ADMIN])
        const isTalentUser = hasRole(RoleName.TALENT)

        // Determine which area to check for verification
        let area: 'care' | 'talent' | undefined
        if (isOrgUser) {
            area = 'care'
        } else if (isTalentUser) {
            area = 'talent'
        }

        // Check verification status first
        const verificationRoute = getVerificationRoute(area)
        if (verificationRoute) {
            return verificationRoute
        }

        // For organisation users, check if they have selected an org
        if (isOrgUser) {
            // Auto-select if there's only one organization
            if (!selectedOrganization.value && organizations.value.length === 1) {
                selectOrganization(organizations.value[0])
                return `/care/${organizations.value[0].code}/dashboard`
            }

            if (!selectedOrganization.value && organizations.value.length > 0) {
                return '/care/select-org'
            }
            return selectedOrganization.value
                ? `/care/${selectedOrganization.value.code}/dashboard`
                : '/care/select-org'
        }

        // For talent users
        if (isTalentUser) {
            return '/talent/dashboard'
        }

        // If authenticated but no recognized role, go to wrong-area page
        // This prevents redirect loops and informs user they don't have proper access
        return '/wrong-area'
    }

    return {
        user,
        organizations,
        selectedOrganization,
        isAuthenticated,
        isInitialized,
        talentHubConsent,
        initializeAuth,
        login,
        loginViaGoogle,
        register,
        logout,
        selectOrganization,
        clearSelectedOrganization,
        hasRole,
        hasAnyRole,
        hasAllRoles,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        getVerificationRoute,
        getDefaultRoute
    }
})
